import { ptBR } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { useDateRangeInput } from '@/hooks/use-date-range-input';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Input } from './input';
import { Label } from './label';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface DateRangeInputProps {
  value?: DateRange;
  onChange?: (date: DateRange) => void;
  showTimeInputs?: boolean;
}

export function DateRangeInput({
  value,
  onChange = () => {},
  showTimeInputs = true,
}: DateRangeInputProps) {
  const { range, hours, handleHoursChange, handleRangeChange, formatHoursString } =
    useDateRangeInput({
      value,
      onChange,
      showTime: showTimeInputs,
    });

  const hasSelection = !!(range?.from || range?.to);

  return (
    <div className="flex flex-col gap-3">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id="dates"
            className={cn(
              'font-normal w-full relative !px-3 overflow-hidden',
              hasSelection ? '!text-foreground' : '!text-muted-foreground/80',
            )}
          >
            <CalendarDays size={20} className="absolute left-3 text-muted-foreground" />

            <span
              className={cn(
                'w-full text-center text-sm truncate px-8',
                hasSelection ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {formatHoursString({ withHours: showTimeInputs })}
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="range"
            selected={range}
            onSelect={(selected) => handleRangeChange(selected)}
            locale={ptBR}
          />

          {showTimeInputs && (
            <div className="flex items-center gap-2 p-2 border-t">
              <div className="w-full">
                <Label htmlFor="time-from" className="sr-only">
                  Start Time
                </Label>
                <Input
                  id="time-from"
                  type="time"
                  step="1"
                  value={hours.from}
                  onChange={(event) => handleHoursChange(event.target.value, 'from')}
                  className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
              </div>

              <span>-</span>

              <div className="w-full">
                <Label htmlFor="time-to" className="sr-only">
                  End Time
                </Label>
                <Input
                  id="time-to"
                  type="time"
                  step="1"
                  value={hours.to}
                  onChange={(event) => handleHoursChange(event.target.value, 'to')}
                  className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
